import styled from 'styled-components';

import { H3, H4 } from '@/components/UI/Typography/Heading';
import { Text } from '@/components/UI/Typography/Text';

export const TitleBar = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-direction: ${(p) => (p.isMobile ? 'column' : 'row')};

  @media (max-width: 639px) {
    gap: 10px;
    width: 100%;
    min-width: 0;
    flex-direction: column;
    margin-bottom: 16px;

    > button {
      min-width: 0;
    }
  }
`;

export const TitleText = styled(H3)`
  margin: 0;
  color: ${(p) => p.theme.colors.gray500};
  font-size: ${(p) => p.theme.fontSizes.lg};

  strong {
    font-weight: 700;
  }

  @media (max-width: 639px) {
    width: 100%;
    min-width: 0;
    font-size: ${(p) => p.theme.fontSizes.md};
    line-height: 1.25;
    text-align: center;
    overflow-wrap: anywhere;
  }
`;

export const FormPanel = styled.form<{ isTablet: boolean }>`
  box-sizing: border-box;
  min-width: 0;
  width: min(100%, 930px);
  padding: ${(p) => (p.isTablet ? '30px 18px' : '34px 52px 40px')};
  border-radius: ${(p) => p.theme.radii.lg};
  background: ${(p) => p.theme.colors.light};
`;

export const SectionTitle = styled(H4)`
  margin: 0 0 18px;
  color: ${(p) => p.theme.colors.primary500};
  font-weight: 700;
`;

export const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 230px minmax(0, 1fr);
  gap: 12px 34px;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 6px;
  }
`;

export const InfoLabel = styled(Text)`
  margin: 0;
  color: ${(p) => p.theme.colors.gray300};
`;

export const InfoValue = styled(Text)`
  margin: 0;
  color: ${(p) => p.theme.colors.gray500};
`;

export const FieldGroup = styled.div`
  margin-bottom: 30px;
`;

export const PromptText = styled(Text)`
  margin: 0 0 12px;
  color: ${(p) => p.theme.colors.gray500};
`;

export const OptionStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const OptionText = styled(Text).attrs({ as: 'span' })`
  margin: 0;
  line-height: 1.5;
`;

export const TextFieldStack = styled.div`
  display: grid;
  gap: 28px;
  margin-bottom: 34px;
`;

export const DisclaimerText = styled(Text)`
  margin: 0 0 30px;
  color: ${(p) => p.theme.colors.gray500};
  line-height: 1.35;
`;

export const DocumentUploadHelp = styled(Text)`
  margin: 0 0 12px;
  color: ${(p) => p.theme.colors.gray400};
  line-height: 1.4;
`;

export const UploadedDocumentList = styled.ul`
  display: grid;
  gap: 8px;
  margin: 12px 0 0;
  padding: 0;
  list-style: none;
`;

export const UploadedDocumentItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid ${(p) => p.theme.colors.gray100};
  border-radius: ${(p) => p.theme.radii.base};
  background: ${(p) => p.theme.colors.gray000};
  color: ${(p) => p.theme.colors.gray500};

  @media (max-width: 639px) {
    align-items: stretch;
    flex-direction: column;

    > button {
      width: 100%;
      min-width: 0;
    }
  }
`;

export const UploadedDocumentName = styled.span`
  min-width: 0;
  overflow-wrap: anywhere;
`;

export const SignatureRow = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${(p) => (p.isMobile ? '10px' : '16px')};
  margin-bottom: 12px;
`;

export const DateText = styled(Text).attrs({ as: 'span' })`
  margin: 0;
  color: ${(p) => p.theme.colors.gray300};
`;

export const SignedBadge = styled(Text).attrs({ as: 'span' })`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 90px;
  height: 40px;
  margin: 0;
  border-radius: ${(p) => p.theme.radii.base};
  background: ${(p) => p.theme.colors.secondary500};
  color: ${(p) => p.theme.colors.light};
  font-weight: 700;
`;

export const SignaturePreview = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 0 0 36px 4px;
`;

export const SignaturePreviewImage = styled.img`
  display: block;
  width: min(220px, 100%);
  max-height: 88px;
  object-fit: contain;
  border: 1px solid ${(p) => p.theme.colors.gray100};
  background: ${(p) => p.theme.colors.light};
`;

export const SignaturePreviewAction = styled.button`
  padding: 0;
  border: 0;
  background: transparent;
  color: ${(p) => p.theme.colors.primary300};
  font: inherit;
  text-decoration: underline;
  cursor: pointer;
`;

export const ActionRow = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: ${(p) => (p.isMobile ? 'column' : 'row')};
  gap: 16px;

  @media (max-width: 639px) {
    width: 100%;
    min-width: 0;
    flex-direction: column;

    > button {
      width: 100%;
      min-width: 0;
    }
  }
`;

export const SubmittedPanel = styled.div<{ isTablet: boolean }>`
  width: min(100%, 930px);
  min-height: 440px;
  padding: ${(p) => (p.isTablet ? '52px 24px' : '72px 48px')};
  border-radius: ${(p) => p.theme.radii.lg};
  background: ${(p) => p.theme.colors.light};
`;

export const SubmittedContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 500px;
  margin: 0 auto;
  text-align: center;
`;

export const SubmittedTitle = styled(H4)`
  margin: 20px 0 28px;
  color: ${(p) => p.theme.colors.gray500};
  font-weight: 700;
`;

export const SubmittedText = styled(Text)`
  margin: 0 0 18px;
  color: ${(p) => p.theme.colors.gray400};
  line-height: 1.35;
`;

export const SubmittedAction = styled.div`
  margin-top: 24px;
`;
